import {
  BaseQueryApi,
  BaseQueryFn
} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from '@reduxjs/toolkit/dist/query/react';
import { Mutex } from 'async-mutex';

import { API_URL, END_POINTS } from '@config';
import ApiTags from '@constants/api-tags';
import {
  ErrorCode,
  OrgLevel403ErrorCodes,
  OrgLevel500ErrorCodes,
  RoleLevel403ErrorCodes
} from '@constants/errorCodes';
import { AccessMode } from '@constants/generic';
import { AUTH, PAGE_NOT_FOUND } from '@constants/routes';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import {
  fetchRealOrgConfigVisibilityFlags,
  isOrgConfigRequest,
  isTrialPassthrough,
  mergeRealOrgConfigVisibilityFlags,
  resolveMockData
} from '@features/trial-mode/mock-data';
import { setBlacklistedModal } from '@layouts/main-header/userSlice';
import { ApiError, GetOrganizationListResponse, TokenResponse } from '@types';
import {
  clearAllLocalStorageItems,
  getLocalStorageItem,
  setLocalStorageItem,
  setTokensInStorage
} from '@utils/storage';
import { getUrlStruct } from '@utils/urlStruct';
import { Org } from 'types/organization';

import { Event, sendMixpanelEvent } from './analytics/mixpanel';

const mutex = new Mutex();

const SLOW_API_THRESHOLD_MS = 4000;

const redirectToLogin = () => {
  clearAllLocalStorageItems();
  if (window.location.pathname === AUTH.LOGIN) return;
  window.location.replace(AUTH.LOGIN);
};

// Endpoints that must keep working even after the active organisation is blacklisted.
const BLACKLIST_ALLOWED_ENDPOINTS = new Set(['getOrganizationList', 'logout']);

// To check whether the active organization is currently blacklisted.
const isActiveOrgBlacklisted = (api: BaseQueryApi): boolean => {
  const state = api.getState() as {
    user?: { blacklistedModal?: { orgList?: Org[] } | null };
  };
  const orgList = state.user?.blacklistedModal?.orgList;
  if (!orgList?.length) return false;
  const currentOrgId = getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);
  return orgList.some(
    (org) =>
      org.id === currentOrgId && org.access_mode === AccessMode.BLACKLISTED
  );
};

// Extract the organisation id a request targets from its URL.
const getRequestOrgId = (args: string | FetchArgs): string => {
  const url = typeof args === 'string' ? args : args?.url;
  return url?.match(/organisation\/([^/?]+)/)?.[1] || '';
};

const getPlatformFromUserAgent = (userAgent: string): string => {
  if (/android/i.test(userAgent)) return 'Web - Android';
  if (/ipad|iphone|ipod/i.test(userAgent)) return 'Web - Ios';
  return 'Web';
};

const parseUserAgent = (ua: string) => {
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua)
    ? 'Mobile'
    : 'Desktop';

  const osMatch =
    ua.match(
      // eslint-disable-next-line no-useless-escape
      /(Android|Windows NT|iPhone OS|iPad; CPU OS|Mac OS X|Linux)[\s\/]?([\d\._]*)/
    ) || [];
  const os =
    osMatch[1]?.replace('Windows NT', 'Windows')?.replace('iPhone OS', 'iOS') ||
    'Unknown OS';
  const osVersion = osMatch[2]?.replace(/_/g, '.') || '';

  const browserMatch =
    // eslint-disable-next-line no-useless-escape
    ua.match(/(Chrome|Safari|Firefox|Edge|SamsungBrowser)\/([\d\.]+)/) || [];
  const browser = browserMatch[1] || 'Unknown Browser';
  const browserVersion = browserMatch[2] || '';

  const appMatch =
    ua.match(/Instagram|FBAV|FBAN|WhatsApp|Snapchat|Line|WeChat/) || [];
  const app = appMatch[0] || ''; // Empty if regular browser

  return `${isMobile}; ${os} ${osVersion}; ${browser} ${browserVersion}${
    app ? `; ${app}` : ''
  }`;
};

const baseQuery = fetchBaseQuery({
  baseUrl: `${API_URL}`,
  prepareHeaders: (headers) => {
    const token = getLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const organizationId = getLocalStorageItem(
      LOCAL_STORAGE_KEYS.ORGANIZATION_ID
    );
    if (organizationId) {
      headers.set('x-org-id', organizationId);
    }

    const platform = window.navigator.userAgent;
    const deviceType = getPlatformFromUserAgent(platform);
    const parsedUserAgent = parseUserAgent(platform);
    headers.set('X-Device-Platform', parsedUserAgent);
    headers.set('X-Device-Type', deviceType);

    return headers;
  }
});

const handle403Error = async (
  errorCode: string,
  api: BaseQueryApi,
  extraOptions: {},
  requestOrgId: string
) => {
  const currentOrgId = getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);

  // Ignore 403s from stale requests that target a different org than the active one
  if (requestOrgId && requestOrgId !== currentOrgId) return;

  const code = errorCode as ErrorCode;
  if (RoleLevel403ErrorCodes.includes(code)) {
    const orgListResult = await baseQuery(
      {
        url: END_POINTS.ORGANIZATION.GET_ORGANIZATION_LIST,
        method: 'GET'
      },
      api,
      extraOptions
    );
    if (orgListResult?.data) {
      const orgList = orgListResult.data as GetOrganizationListResponse;
      const organizationData = orgList.find(
        (org: Org) => org.id === currentOrgId
      );

      if (organizationData) {
        const branchId = organizationData.default_branch_id;
        const branchName = organizationData.default_branch_name;
        setLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID, branchId);
        setLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_NAME, branchName);
      }
    }
    window.location.replace(PAGE_NOT_FOUND);
  } else if (OrgLevel403ErrorCodes.includes(code)) {
    // The current org is forbidden. If it is specifically blacklisted, keep the
    // session and let the UI show the blacklisted modal instead of logging out.
    const orgListResult = await baseQuery(
      {
        url: END_POINTS.ORGANIZATION.GET_ORGANIZATION_LIST,
        method: 'GET'
      },
      api,
      extraOptions
    );
    const orgList = (orgListResult?.data as GetOrganizationListResponse) || [];
    const currentOrg = orgList.find((org: Org) => org.id === currentOrgId);

    if (currentOrg?.access_mode === AccessMode.BLACKLISTED) {
      api.dispatch(
        setBlacklistedModal({
          orgList
        })
      );
      return;
    }

    redirectToLogin();
  }
};

const handle500Error = (
  errorCode: string,
  userType: string,
  requestOrgId: string
) => {
  // Ignore 500s from stale requests targeting a different org than the active
  // one so they don't force a logout after switching accounts.
  const currentOrgId = getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);
  if (requestOrgId && requestOrgId !== currentOrgId) return;

  if (
    userType !== 'admin' &&
    OrgLevel500ErrorCodes.includes(errorCode as ErrorCode)
  ) {
    redirectToLogin();
  }
};

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const state = api.getState() as {
    trial?: {
      isTrialMode: boolean;
      originalAccountType?: string | null;
      originalStorageValues?: Record<string, string> | null;
    };
  };
  const realOrgId =
    state.trial?.originalStorageValues?.[LOCAL_STORAGE_KEYS.ORGANIZATION_ID];
  if (state.trial?.isTrialMode && !isTrialPassthrough(args, realOrgId)) {
    const mockResponse = resolveMockData(
      args,
      state.trial?.originalAccountType || undefined
    );
    if (mockResponse !== undefined) {
      if (realOrgId && isOrgConfigRequest(args)) {
        const realFlags = await fetchRealOrgConfigVisibilityFlags(realOrgId);
        return {
          data: mergeRealOrgConfigVisibilityFlags(mockResponse, realFlags)
        };
      }
      return { data: mockResponse };
    }
  }

  // Block all requests once the active org is blacklisted, except allowed endpoints.
  if (
    !BLACKLIST_ALLOWED_ENDPOINTS.has(api.endpoint) &&
    isActiveOrgBlacklisted(api)
  ) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'Request blocked: active organisation is blacklisted'
      }
    };
  }

  await mutex.waitForUnlock();

  // The org this request actually targets. Prefer the org id embedded in the
  // request URL over the stored one, so a stale request fired against a
  // previous org
  const requestOrgId =
    getRequestOrgId(args) ||
    getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);
  const requestSentAt = new Date();
  const startTime = performance.now();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshToken = getLocalStorageItem(
          LOCAL_STORAGE_KEYS.REFRESH_TOKEN
        );
        const mobile = getLocalStorageItem(LOCAL_STORAGE_KEYS.MOBILE);
        const refreshResult = await baseQuery(
          {
            url: END_POINTS.AUTH.REFRESH,
            method: 'POST',
            body: { refresh_token: refreshToken, mobile: String(mobile) }
          },
          api,
          extraOptions
        );
        if (refreshResult.data) {
          setTokensInStorage(refreshResult.data as TokenResponse);
          // Retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          redirectToLogin();
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  } else if (result.error) {
    const error = result.error as ApiError;
    const errorCode = error.data?.errorCode;
    const isStaleOrgRequest =
      !!requestOrgId &&
      requestOrgId !== getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID);

    if (
      errorCode === ErrorCode.ERR_ORGANISATION_NOT_FOUND &&
      !isStaleOrgRequest
    ) {
      redirectToLogin();
    }

    if (error.status === 403 && errorCode) {
      await handle403Error(errorCode, api, extraOptions, requestOrgId);
    } else if (error.status === 500 && errorCode) {
      const userType = getLocalStorageItem(LOCAL_STORAGE_KEYS.USER_TYPE);
      handle500Error(errorCode, userType, requestOrgId);
    }
  }

  const networkDuration = performance.now() - startTime;
  if (networkDuration > SLOW_API_THRESHOLD_MS) {
    const url = typeof args === 'string' ? args : args?.url;
    const method = typeof args === 'string' ? 'GET' : args?.method || 'GET';
    const userState = api.getState() as {
      user?: {
        currentOrg?: { name?: string; role_name?: string };
        currentUser?: { is_internal?: boolean };
        rolesAndPermissions?: { role_name?: string };
      };
    };

    sendMixpanelEvent(Event.SLOW_API_RESPONSE, {
      network_duration_ms: Math.round(networkDuration),
      url,
      method: method.toLowerCase(),
      endpoint: getUrlStruct(url),
      org_name: userState.user?.currentOrg?.name || '',
      module: 'web_app',
      request_sent_at: requestSentAt.toISOString(),
      response_received_at: new Date().toISOString(),
      user_role:
        userState.user?.rolesAndPermissions?.role_name ||
        userState.user?.currentOrg?.role_name ||
        '',
      internal: Boolean(userState.user?.currentUser?.is_internal)
    });
  }

  return result;
};

// eslint-disable-next-line import/prefer-default-export
export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  refetchOnMountOrArgChange: false,
  refetchOnReconnect: false,
  tagTypes: Object.values(ApiTags)
});
